if (!customElements.get('ta-atr-switcher')) {
  class TaAtrSwitcher extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = this.tabs
        .map((tab) => document.getElementById(tab.getAttribute('aria-controls')))
        .filter(Boolean);
      this.items = Array.from(this.querySelectorAll('[data-switcher-item]'));
      this.copyPanels = Array.from(this.querySelectorAll('[data-switcher-copy]'));
      this.surfaces = Array.from(this.querySelectorAll('[data-switcher-surface]'));
      this.stepButtons = Array.from(this.querySelectorAll('[data-switcher-step]'));
      this.transitionMode = this.dataset.transition || '';

      if (this.transitionMode === 'comparison') {
        this.tabs.forEach((tab) => {
          tab.tabIndex = -1;
        });
        this.copyPanels.forEach((copy, index) => {
          copy.tabIndex = this.tabs[index]?.getAttribute('aria-selected') === 'true' ? 0 : -1;
        });
        this.measureComparisonItems();
        this.resizeHandler = () => {
          window.cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = window.requestAnimationFrame(() => this.measureComparisonItems());
        };
        window.addEventListener('resize', this.resizeHandler);
        document.fonts?.ready?.then(() => this.measureComparisonItems());
      }

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      this.copyPanels.forEach((copy, index) => {
        copy.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      this.stepButtons.forEach((button) => {
        button.addEventListener('click', () => this.step(Number.parseInt(button.dataset.switcherStep, 10)));
      });

      const defaultIndex = Number.parseInt(this.dataset.defaultIndex, 10);
      if (Number.isInteger(defaultIndex) && defaultIndex >= 0 && defaultIndex < this.tabs.length) {
        this.select(defaultIndex);
      }
    }

    disconnectedCallback() {
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      this.clearPerformanceTransition();
    }

    select(index, focus = false, directionHint = 0) {
      if (index < 0 || index >= this.tabs.length) return;

      const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      if (selectedIndex === index) {
        if (focus) {
          if (this.transitionMode === 'comparison') {
            this.copyPanels[index]?.focus();
          } else {
            this.tabs[index]?.focus();
          }
        }
        return;
      }

      let previousPositions = null;
      if (this.transitionMode === 'comparison') {
        this.items.forEach((item, itemIndex) => {
          if (item.taAtrFlipAnimation) {
            item.taAtrFlipAnimation.commitStyles?.();
            item.taAtrFlipAnimation.cancel();
          }

          const surface = this.surfaces[itemIndex];
          if (surface?.taAtrSurfaceAnimation) {
            surface.taAtrSurfaceAnimation.commitStyles?.();
            surface.taAtrSurfaceAnimation.cancel();
          }
        });
        previousPositions = new Map(this.items.map((item) => [item, item.getBoundingClientRect()]));
        this.items.forEach((item, itemIndex) => {
          item.style.removeProperty('transform');
          this.surfaces[itemIndex]?.style.removeProperty('transform');
        });
      }

      this.tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', String(selected));
        if (this.dataset.presentation === 'accordion') {
          tab.setAttribute('aria-expanded', String(selected));
        }
        tab.tabIndex = this.transitionMode === 'comparison' ? -1 : (selected ? 0 : -1);
      });

      this.items.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === index);
      });

      this.copyPanels.forEach((copy, copyIndex) => {
        const selected = copyIndex === index;
        this.updatePanelState(copy, selected);
        copy.tabIndex = selected && this.transitionMode === 'comparison' ? 0 : -1;
      });

      if (this.transitionMode === 'performance') {
        this.updatePerformancePanels(selectedIndex, index, directionHint);
      } else {
        this.panels.forEach((panel, panelIndex) => {
          this.updatePanelState(panel, panelIndex === index);
        });
      }

      if (previousPositions) this.animateItems(previousPositions);

      if (focus) {
        if (this.transitionMode === 'comparison') {
          this.copyPanels[index]?.focus();
        } else {
          this.tabs[index]?.focus();
        }
      }

      if (this.transitionMode === 'performance') {
        this.style.setProperty('--ta-atr-active-tab', String(index));
      }
    }

    clearPerformanceTransition() {
      if (this.performanceCleanupTimer) {
        window.clearTimeout(this.performanceCleanupTimer);
        this.performanceCleanupTimer = null;
      }

      if (this.performanceLeavingPanel && this.performanceTransitionEndHandler) {
        this.performanceLeavingPanel.removeEventListener('transitionend', this.performanceTransitionEndHandler);
      }

      this.panels?.forEach((panel) => {
        panel.classList.remove('is-leaving', 'is-positioning');
      });
      this.performanceLeavingPanel = null;
      this.performanceTransitionEndHandler = null;
    }

    updatePerformancePanels(previousIndex, nextIndex, directionHint = 0) {
      this.clearPerformanceTransition();

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const direction = directionHint || (nextIndex > previousIndex ? 1 : -1);
      const incomingPanel = this.panels[nextIndex];
      const outgoingPanel = this.panels[previousIndex];

      if (!reducedMotion && incomingPanel && outgoingPanel && incomingPanel !== outgoingPanel) {
        incomingPanel.classList.remove('is-before', 'is-after');
        incomingPanel.classList.add('is-positioning', direction > 0 ? 'is-after' : 'is-before');
        incomingPanel.getBoundingClientRect();
      }

      this.panels.forEach((panel, panelIndex) => {
        panel.classList.remove('is-before', 'is-after');
        if (this.dataset.presentation === 'previews') {
          panel.style.setProperty('--ta-atr-panel-offset', String(panelIndex - nextIndex));
        }

        if (panelIndex === nextIndex) {
          panel.classList.remove('is-positioning');
        } else if (!reducedMotion && panelIndex === previousIndex && outgoingPanel !== incomingPanel) {
          panel.classList.add('is-leaving', direction > 0 ? 'is-before' : 'is-after');
        } else {
          panel.classList.add(panelIndex < nextIndex ? 'is-before' : 'is-after');
        }

        this.updatePanelState(panel, panelIndex === nextIndex);
      });

      if (reducedMotion || !outgoingPanel || outgoingPanel === incomingPanel) return;

      const finishTransition = (event) => {
        if (event && event.target !== outgoingPanel) return;
        if (event && event.propertyName !== 'transform') return;
        if (this.performanceLeavingPanel !== outgoingPanel) return;

        outgoingPanel.classList.remove('is-leaving');
        outgoingPanel.removeEventListener('transitionend', finishTransition);
        window.clearTimeout(this.performanceCleanupTimer);
        this.performanceCleanupTimer = null;
        this.performanceLeavingPanel = null;
        this.performanceTransitionEndHandler = null;
      };

      this.performanceLeavingPanel = outgoingPanel;
      this.performanceTransitionEndHandler = finishTransition;
      outgoingPanel.addEventListener('transitionend', finishTransition);
      this.performanceCleanupTimer = window.setTimeout(() => finishTransition(), 500);
    }

    measureComparisonItems() {
      if (this.transitionMode !== 'comparison' || !this.items.length) return;

      this.classList.add('is-measuring');
      this.items.forEach((item, index) => {
        const tab = this.tabs[index];
        const copy = this.copyPanels[index];
        if (!tab || !copy) return;

        const itemWasActive = item.classList.contains('is-active');
        const copyWasActive = copy.classList.contains('is-active');

        item.classList.remove('is-active');
        copy.classList.remove('is-active');
        item.style.setProperty('--ta-atr-collapsed-width', `${Math.ceil(tab.scrollWidth)}px`);
        item.style.setProperty('--ta-atr-collapsed-height', `${Math.ceil(tab.getBoundingClientRect().height)}px`);

        item.classList.add('is-active');
        copy.classList.add('is-active');
        copy.style.setProperty('--ta-atr-expanded-copy-height', `${Math.ceil(copy.scrollHeight)}px`);
        const copyStyle = window.getComputedStyle(copy);
        const verticalPadding = Number.parseFloat(copyStyle.top) + Number.parseFloat(copyStyle.bottom);
        item.style.setProperty('--ta-atr-expanded-height', `${Math.ceil(copy.scrollHeight + verticalPadding)}px`);

        item.classList.toggle('is-active', itemWasActive);
        copy.classList.toggle('is-active', copyWasActive);
      });
      this.classList.remove('is-measuring');
    }

    updatePanelState(panel, selected) {
      if (!panel) return;

      if (!this.transitionMode) {
        panel.hidden = !selected;
        return;
      }

      panel.hidden = false;
      panel.classList.toggle('is-active', selected);
      panel.setAttribute('aria-hidden', String(!selected));
      if (selected) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    }

    animateItems(previousPositions) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const currentPositions = new Map(this.items.map((item) => [item, item.getBoundingClientRect()]));
      this.items.forEach((item, itemIndex) => {
        const previous = previousPositions.get(item);
        const current = currentPositions.get(item);
        if (!previous) return;

        const translateX = previous.left - current.left;
        const translateY = previous.top - current.top;
        if (Math.abs(translateX) >= 1 || Math.abs(translateY) >= 1) {
          item.classList.add('is-animating');
          const itemAnimation = item.animate(
            [
              { transform: `translate(${translateX}px, ${translateY}px)` },
              { transform: 'none' }
            ],
            { duration: 400, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
          );
          item.taAtrFlipAnimation = itemAnimation;
          itemAnimation.finished
            .catch(() => {})
            .finally(() => {
              if (item.taAtrFlipAnimation === itemAnimation) item.classList.remove('is-animating');
            });
        }

        const scaleX = current.width > 0 ? previous.width / current.width : 1;
        const scaleY = current.height > 0 ? previous.height / current.height : 1;
        const surface = this.surfaces[itemIndex];
        if (surface && (Math.abs(scaleX - 1) >= 0.01 || Math.abs(scaleY - 1) >= 0.01)) {
          surface.classList.add('is-animating');
          const surfaceAnimation = surface.animate(
            [
              { transform: `scale(${scaleX}, ${scaleY})` },
              { transform: 'none' }
            ],
            { duration: 400, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
          );
          surface.taAtrSurfaceAnimation = surfaceAnimation;
          surfaceAnimation.finished
            .catch(() => {})
            .finally(() => {
              if (surface.taAtrSurfaceAnimation === surfaceAnimation) surface.classList.remove('is-animating');
            });
        }
      });
    }

    step(direction) {
      if (!Number.isInteger(direction) || !this.tabs.length) return;
      const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      const currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
      const nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
      this.select(nextIndex, false, Math.sign(direction));
    }

    onKeydown(event, index) {
      const forwardKeys = ['ArrowRight', 'ArrowDown'];
      const backwardKeys = ['ArrowLeft', 'ArrowUp'];
      let nextIndex = index;
      let directionHint = 0;

      if (forwardKeys.includes(event.key)) {
        nextIndex = (index + 1) % this.tabs.length;
        directionHint = 1;
      }
      if (backwardKeys.includes(event.key)) {
        nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        directionHint = -1;
      }
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      if (nextIndex === index && !['Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      this.select(nextIndex, true, directionHint);
    }
  }

  customElements.define('ta-atr-switcher', TaAtrSwitcher);

  document.addEventListener('keydown', (event) => {
    if (!['Enter', ' '].includes(event.key)) return;
    if (!document.querySelector('#main-content .product--ta-refined-camera-ui')) return;

    const trigger = event.target.closest('.ta-atr-audience__image-trigger');
    if (!trigger) return;

    event.preventDefault();
    trigger.click();
  });
}
